import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ShieldWarning, ArrowClockwise, CheckCircle, CircleNotch, Eye, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { Footer } from "@/components/Footer";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_TONE = {
  pending: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  rework: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
  hold: "bg-[#F2F4F7] text-[#475467] border-[#D0D5DD]",
  partial: "bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]",
  approved: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  posted: "bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]",
};
const STATUS_LABEL = {
  pending: "Pending Quality Decision",
  rework: "Rework - Blocked",
  hold: "On Hold - Blocked",
  partial: "Part Cleared - Part With Quality",
  approved: "Cleared - Production To Post",
  posted: "Posted To SAP",
};

// Oct 27 2026, user's spec point 5: quality views the notes, enters the figures
// and rules on the scrap. Nov 4 2026: quality now SPLITS the quantity it holds
// - "1 pcs confirm, 1 pcs scrap, 1 pcs hold" - production posts the cleared
// part at once, and the rework/hold pieces come back for a second verdict that
// can only be confirm or scrap.
const NoteCard = ({ note, canDecide, onDecided }) => {
  const requested = note.requested || {};
  const pending = note.pending || {};
  const firstRound = note.status === "pending";
  const toDecide = note.to_decide_quantity ?? (firstRound
    ? Number(requested.confirmed_quantity || 0) + Number(requested.confirmed_scrap || 0)
    : Number(pending.rework_quantity || 0) + Number(pending.hold_quantity || 0));
  const [accepted, setAccepted] = useState(firstRound ? String(requested.confirmed_quantity ?? "") : "0");
  const [scrap, setScrap] = useState(firstRound ? String(requested.confirmed_scrap ?? "") : "0");
  const [rework, setRework] = useState("0");
  const [hold, setHold] = useState("0");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState("");
  const blocked = ["pending", "rework", "hold", "partial"].includes(note.status);
  const unit = formatUnit(note.unit_code);
  // Every verdict quality has given on this lot, newest last, plus what is
  // still pending - the table the user asked for.
  const rounds = note.decisions?.length ? note.decisions : (note.decision ? [note.decision] : []);
  const reportedTotal = note.reported_total ?? (Number(requested.confirmed_quantity || 0) + Number(requested.confirmed_scrap || 0));
  const pendingQty = note.to_decide_quantity ?? toDecide;

  const split = [accepted, scrap, rework, hold].map((v) => Number(v || 0));
  const decided = Math.round(split.reduce((a, b) => a + b, 0) * 10000) / 10000;
  const left = Math.round((toDecide - decided) * 10000) / 10000;
  const parkedNow = split[2] + split[3] > 0;
  const canSubmit = Math.abs(left) < 1e-6 && decided > 0 && (!parkedNow || remark.trim().length > 0);

  const decide = async () => {
    setBusy("decide");
    try {
      const { data } = await axios.post(`${API}/quality-decisions/${note._id}/decide`, {
        outcome: "decide",
        physical_accepted_quantity: split[0],
        approved_scrap_quantity: split[1],
        rework_quantity: split[2],
        hold_quantity: split[3],
        remark: remark.trim(),
      });
      toast.success(
        parkedNow
          ? `${note._id}: ${split[0] + split[1]} ${unit} cleared, ${split[2] + split[3]} ${unit} stays with quality`
          : `${note._id}: cleared - production can post ${split[0]} good and ${split[1]} scrap`,
      );
      onDecided(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not record that decision");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white border border-[#D0D5DD] rounded-sm" data-testid={`qdn-card-${note._id}`}>
      <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3 flex-wrap">
        <span className="font-mono font-bold text-[#175CD3]">{note._id}</span>
        <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[note.status] || ""}`} data-testid={`qdn-status-${note._id}`}>
          {STATUS_LABEL[note.status] || note.status}
        </Badge>
        <span className="text-xs text-[#667085]">
          Lot <span className="font-mono font-bold text-[#101828]">{note.production_lot_id}</span> · {note.main_output_product || "—"} ·{" "}
          {note.reporting_point_description || note.reporting_point_id || "—"} · site {note.site_id || "—"}
        </span>
        <span className="text-xs text-[#98A2B3] ml-auto">Raised by {note.raised_by} · {when(note.raised_at)}</span>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
        <div className="space-y-2 text-[13px]">
          <h3 className="text-xs font-bold text-[#344054] uppercase tracking-wide">What production reported</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-2.5 py-1.5">
              <div className="text-[11px] text-[#667085]">Confirmed Qty</div>
              <div className="font-bold tabular-nums">{fmt(requested.confirmed_quantity)} {formatUnit(note.unit_code)}</div>
            </div>
            <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2.5 py-1.5">
              <div className="text-[11px] text-[#B54708]">Scrap Reported</div>
              <div className="font-bold tabular-nums text-[#B54708]" data-testid={`qdn-scrap-${note._id}`}>
                {fmt(requested.confirmed_scrap)} {formatUnit(note.unit_code)}
              </div>
            </div>
          </div>
          <div className="text-[12px] text-[#475467]">
            Scrap reason: <span className="font-bold">{requested.deviation_reason_code || "—"}</span>
            {note.scrap_reason_description && (
              <span className="font-bold text-[#B54708]" data-testid={`qdn-scrap-reason-${note._id}`}> - {note.scrap_reason_description}</span>
            )}
          </div>
          <div className="text-[12px] text-[#475467]">
            Production model: <span className="font-mono">{note.production_model_id}</span>
          </div>
          {/* Nov 4 2026, user's ask: "need to show all decision as table with
              decision pending status against the lot, so that QC user can
              easily understand what decision he took on that lot earlier &
              what is pending". */}
          {(rounds.length > 0 || pendingQty > 0) && (
            <div className="border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid={`qdn-decision-table-${note._id}`}>
              <table className="w-full text-[12px] border-collapse">
                <thead>
                  <tr className="bg-[#F9FAFB] text-[10px] uppercase tracking-wide text-[#475467]">
                    <th className="px-2 py-1.5 text-left font-bold">#</th>
                    <th className="px-2 py-1.5 text-left font-bold">Decided</th>
                    <th className="px-2 py-1.5 text-right font-bold text-[#027A48]">Confirm</th>
                    <th className="px-2 py-1.5 text-right font-bold text-[#B54708]">Scrap</th>
                    <th className="px-2 py-1.5 text-right font-bold text-[#B42318]">Rework</th>
                    <th className="px-2 py-1.5 text-right font-bold">Hold</th>
                    <th className="px-2 py-1.5 text-left font-bold">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EAECF0]">
                  {rounds.map((d, i) => (
                    <tr key={i} data-testid={`qdn-decision-row-${note._id}-${i + 1}`}>
                      <td className="px-2 py-1.5 font-bold text-[#667085]">{i + 1}</td>
                      <td className="px-2 py-1.5 text-[#475467] whitespace-nowrap">
                        {when(d.decided_at)}<span className="text-[#98A2B3]"> · {d.decided_by}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold text-[#027A48]">{fmt(d.physical_accepted_quantity)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold text-[#B54708]">{fmt(d.approved_scrap_quantity)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold text-[#B42318]">{fmt(d.rework_quantity)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmt(d.hold_quantity)}</td>
                      <td className="px-2 py-1.5 text-[#475467] italic min-w-[160px]" data-testid={`qdn-decision-remark-${note._id}-${i + 1}`}>
                        {d.remark ? `"${d.remark}"` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={pendingQty > 0 ? "bg-[#FFFAEB] text-[#B54708]" : "bg-[#ECFDF3] text-[#027A48]"}>
                    <td colSpan={7} className="px-2 py-1.5 font-bold" data-testid={`qdn-pending-row-${note._id}`}>
                      {pendingQty > 0
                        ? `Decision pending on ${fmt(pendingQty)} ${unit}`
                        : `All ${fmt(reportedTotal)} ${unit} decided`}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {canDecide && blocked && (
          <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-xs font-bold text-[#344054] uppercase tracking-wide">Quality decision</h3>
              <span className="text-[11px] text-[#667085]" data-testid={`qdn-to-decide-${note._id}`}>
                {fmt(toDecide)} {unit} to decide
                {!firstRound && <span className="text-[#B54708]"> (back from {Number(pending.rework_quantity) > 0 ? "rework" : "hold"})</span>}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs font-bold text-[#027A48]">Confirm Qty</Label>
                <Input
                  type="number" min="0" step="any" value={accepted}
                  onChange={(e) => setAccepted(e.target.value)}
                  className="h-9 text-right tabular-nums"
                  data-testid={`qdn-accepted-input-${note._id}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-[#B54708]">Scrap Qty</Label>
                <Input
                  type="number" min="0" step="any" value={scrap}
                  onChange={(e) => setScrap(e.target.value)}
                  className="h-9 text-right tabular-nums"
                  data-testid={`qdn-scrap-input-${note._id}`}
                />
              </div>
              {/* Nov 4 2026: rework and hold are a waiting room - a piece parked
                  here comes back for confirm or scrap, so the second round
                  offers only those two. */}
              {firstRound && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-[#B42318]">Rework Qty</Label>
                    <Input
                      type="number" min="0" step="any" value={rework}
                      onChange={(e) => setRework(e.target.value)}
                      className="h-9 text-right tabular-nums"
                      data-testid={`qdn-rework-input-${note._id}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-[#475467]">Hold Qty</Label>
                    <Input
                      type="number" min="0" step="any" value={hold}
                      onChange={(e) => setHold(e.target.value)}
                      className="h-9 text-right tabular-nums"
                      data-testid={`qdn-hold-input-${note._id}`}
                    />
                  </div>
                </>
              )}
            </div>
            <div
              className={`text-[12px] rounded-sm px-2.5 py-1.5 border tabular-nums ${
                Math.abs(left) < 1e-6
                  ? "text-[#027A48] bg-[#ECFDF3] border-[#ABEFC6]"
                  : "text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]"
              }`}
              data-testid={`qdn-split-balance-${note._id}`}
            >
              {Math.abs(left) < 1e-6
                ? `All ${fmt(toDecide)} ${unit} decided`
                : left > 0
                  ? `${fmt(left)} ${unit} still to decide`
                  : `${fmt(-left)} ${unit} more than quality holds`}
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Remark {parkedNow ? "*" : ""}</Label>
              <Textarea
                value={remark} rows={2}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="What production should know - needed for rework or hold"
                data-testid={`qdn-remark-input-${note._id}`}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button className="h-9" disabled={!!busy || !canSubmit} onClick={decide} data-testid={`qdn-submit-${note._id}`}>
                <CheckCircle size={14} weight="bold" className="mr-1.5" />
                {busy ? "Recording..." : "Submit Decision"}
              </Button>
            </div>
            <p className="text-[11px] text-[#667085]">
              Nothing here posts to SAP. Production posts the confirmed and scrap pieces from their own screen;
              rework and hold pieces stay blocked and come back to you for a confirm or scrap call.
            </p>
          </div>
        )}
        {!canDecide && blocked && (
          <div className="text-[12px] text-[#667085] self-center" data-testid={`qdn-readonly-${note._id}`}>
            Waiting on the quality team - you can see this note but not decide it.
          </div>
        )}
      </div>
    </div>
  );
};

export default function QualityDecisionPage() {
  const [notes, setNotes] = useState([]);
  const [canDecide, setCanDecide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  // Nov 4 2026, user's ask: "do not show all the request as view - show
  // requests by the list, when user click it should be view".
  const [openNoteId, setOpenNoteId] = useState(null);

  const load = useCallback(async (all) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quality-decisions`, { params: { status: all ? "" : "open" } });
      setNotes(data.notes || []);
      setCanDecide(!!data.can_decide);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the Quality Decision Notes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(showAll); }, [load, showAll]);

  const onDecided = (updated) => setNotes((prev) => prev.map((n) => (n._id === updated._id ? updated : n)));
  const openNote = notes.find((n) => n._id === openNoteId) || null;

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ShieldWarning size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Quality Decision</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
      </header>
      <main className="flex-1 max-w-[1280px] w-full mx-auto px-4 py-6 space-y-4" data-testid="quality-decision-page">
        <div className="flex items-center gap-3 flex-wrap">
          <ShieldWarning size={20} className="text-[#0E7C86]" weight="fill" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">Quality Decision</h1>
          <span className="text-sm text-[#475467]">
            Scrap reported on a production model with Quality Activity waits here - the lot stays blocked until you decide
          </span>
          <span className="flex items-center gap-1.5 text-xs" data-testid="qdn-counts">
            <span className="px-2 py-0.5 rounded-sm border border-[#FEDF89] bg-[#FFFAEB] text-[#B54708] font-bold" data-testid="qdn-count-open">
              Waiting on you: {notes.filter((n) => ["pending", "rework", "hold", "partial"].includes(n.status)).length}
            </span>
            <span className="px-2 py-0.5 rounded-sm border border-[#EAECF0] bg-white text-[#475467]" data-testid="qdn-count-shown">
              Shown: {notes.length}
            </span>
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" className="h-8 bg-white" asChild data-testid="qdn-report-link">
              <Link to="/quality-decision-report"><FileText size={14} className="mr-1.5" /> Report</Link>
            </Button>
            <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => setShowAll((v) => !v)} data-testid="qdn-toggle-all">
              {showAll ? "Show open only" : "Show all notes"}
            </Button>
            <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => load(showAll)} data-testid="qdn-refresh">
              <ArrowClockwise size={14} className="mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        {loading && <div className="py-12 text-center text-[#98A2B3]"><CircleNotch size={20} className="animate-spin inline" /></div>}
        {!loading && notes.length === 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm py-12 text-center text-[#667085]" data-testid="qdn-empty">
            No Quality Decision Notes {showAll ? "at all" : "waiting"} - nothing is blocked.
          </div>
        )}
        {!loading && notes.length > 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="qdn-list-table">
              <thead>
                <tr>
                  {["Notification", "Lot", "Output Product", "Reporting Point", "Scrap", "Status", "Raised By", "Raised", ""].map((h) => (
                    <th key={h} className="bg-[#F2F4F7] border-b border-[#D0D5DD] px-2.5 py-2 text-left text-[11px] font-bold text-[#344054] font-heading uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {notes.map((note) => (
                  <tr
                    key={note._id}
                    onClick={() => setOpenNoteId(note._id)}
                    className="border-b border-[#EAECF0] last:border-0 hover:bg-[#F9FAFB] cursor-pointer"
                    data-testid={`qdn-row-${note._id}`}
                  >
                    <td className="px-2.5 py-2 font-mono font-bold text-[#175CD3] whitespace-nowrap">{note._id}</td>
                    <td className="px-2.5 py-2 font-mono font-bold text-[#101828]">{note.production_lot_id}</td>
                    <td className="px-2.5 py-2 text-[#475467]">{note.main_output_product || "—"}</td>
                    <td className="px-2.5 py-2 text-[#475467]">
                      {note.reporting_point_id}{note.reporting_point_description ? ` - ${note.reporting_point_description}` : ""}
                    </td>
                    <td className="px-2.5 py-2 text-right tabular-nums font-bold text-[#B54708]">
                      {fmt((note.requested || {}).confirmed_scrap)} {formatUnit(note.unit_code)}
                    </td>
                    <td className="px-2.5 py-2">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[note.status] || ""}`} data-testid={`qdn-status-${note._id}`}>
                        {STATUS_LABEL[note.status] || note.status}
                      </Badge>
                    </td>
                    <td className="px-2.5 py-2 text-[#475467] whitespace-nowrap">{note.raised_by}</td>
                    <td className="px-2.5 py-2 text-[#98A2B3] whitespace-nowrap">{when(note.raised_at)}</td>
                    <td className="px-2.5 py-2 text-right">
                      <Button variant="ghost" size="sm" className="h-7 text-[#0E7C86]" data-testid={`qdn-open-${note._id}`}>
                        <Eye size={14} className="mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={!!openNote} onOpenChange={(v) => !v && setOpenNoteId(null)}>
        <DialogContent className="sm:max-w-[920px] p-0 gap-0" data-testid="qdn-detail-dialog">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="font-heading text-base">Quality Decision Note {openNote?._id}</DialogTitle>
          </DialogHeader>
          {openNote && (
            <div className="p-3">
              <NoteCard note={openNote} canDecide={canDecide} onDecided={(updated) => { onDecided(updated); setOpenNoteId(null); }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
